"use client"

import { useState } from "react"
import { Plus, Trash2, Download, Settings, Save } from "lucide-react"

const AdminPanel = ({ devices, onAddDevice, onRemoveDevice, onUpdateThresholds, onExportLogs }) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newDevice, setNewDevice] = useState({
    name: "",
    location: { lat: "", lng: "", address: "" },
    chemicalThreshold: 0.5,
    vibrationThreshold: 2.0,
  })
  const [thresholds, setThresholds] = useState({})

  const handleAddDevice = (e) => {
    e.preventDefault()
    const device = {
      id: `SNT-${String(devices.length + 1).padStart(3, "0")}`,
      ...newDevice,
      location: {
        ...newDevice.location,
        lat: Number.parseFloat(newDevice.location.lat),
        lng: Number.parseFloat(newDevice.location.lng),
      },
      status: "active",
      battery: 100,
      lastSignal: new Date().toISOString(),
      health: "excellent",
      sensors: {
        chemical: {
          status: "normal",
          value: 0.01,
          threshold: newDevice.chemicalThreshold,
        },
        vibration: {
          status: "normal",
          value: 0.05,
          threshold: newDevice.vibrationThreshold,
        },
      },
    }

    onAddDevice(device)
    setNewDevice({
      name: "",
      location: { lat: "", lng: "", address: "" },
      chemicalThreshold: 0.5,
      vibrationThreshold: 2.0,
    })
    setShowAddForm(false)
  }

  const handleThresholdChange = (deviceId, sensor, value) => {
    setThresholds((prev) => ({
      ...prev,
      [deviceId]: {
        ...prev[deviceId],
        [sensor]: Number.parseFloat(value),
      },
    }))
  }

  const handleSaveThresholds = (deviceId) => {
    if (thresholds[deviceId]) {
      onUpdateThresholds(deviceId, thresholds[deviceId])
    }
  }

  return (
    <div className="space-y-6">
      {/* Device Management */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h3 className="card-title">Device Management</h3>
            <p className="card-subtitle">Add, remove, and configure devices</p>
          </div>
          <button onClick={() => setShowAddForm(!showAddForm)} className="btn btn-primary flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add Device
          </button>
        </div>

        {showAddForm && (
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleAddDevice}>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Add New Device</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Device Name</label>
                  <input
                    type="text"
                    required
                    value={newDevice.name}
                    onChange={(e) => setNewDevice((prev) => ({ ...prev, name: e.target.value }))}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    required
                    value={newDevice.location.address}
                    onChange={(e) =>
                      setNewDevice((prev) => ({
                        ...prev,
                        location: { ...prev.location, address: e.target.value },
                      }))
                    }
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={newDevice.location.lat}
                    onChange={(e) =>
                      setNewDevice((prev) => ({
                        ...prev,
                        location: { ...prev.location, lat: e.target.value },
                      }))
                    }
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={newDevice.location.lng}
                    onChange={(e) =>
                      setNewDevice((prev) => ({
                        ...prev,
                        location: { ...prev.location, lng: e.target.value },
                      }))
                    }
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Chemical Threshold (ppm)</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={newDevice.chemicalThreshold}
                    onChange={(e) =>
                      setNewDevice((prev) => ({ ...prev, chemicalThreshold: Number.parseFloat(e.target.value) }))
                    }
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Vibration Threshold (Hz)</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={newDevice.vibrationThreshold}
                    onChange={(e) =>
                      setNewDevice((prev) => ({ ...prev, vibrationThreshold: Number.parseFloat(e.target.value) }))
                    }
                    className="form-input"
                  />
                </div>
              </div>
              <div className="mt-4 flex space-x-3">
                <button type="submit" className="btn btn-primary">
                  Add Device
                </button>
                <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Device</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Location</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {devices.map((device) => (
                <tr key={device.id} className="table-row">
                  <td className="table-cell">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{device.name}</div>
                      <div className="text-sm text-gray-500">{device.id}</div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-md device-status-${device.status}`}
                    >
                      {device.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="table-cell text-sm text-gray-900">{device.location.address}</td>
                  <td className="table-cell">
                    <button onClick={() => onRemoveDevice(device.id)} className="btn btn-danger btn-sm">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Threshold Configuration */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            <Settings className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="card-title">Threshold Configuration</h3>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {devices.map((device) => (
            <div key={device.id} className="border border-gray-200 rounded-md p-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">{device.name}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="form-group mb-0">
                  <label className="form-label">Chemical Threshold (ppm)</label>
                  <input
                    type="number"
                    step="0.1"
                    defaultValue={device.sensors.chemical.threshold}
                    onChange={(e) => handleThresholdChange(device.id, "chemical", e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Vibration Threshold (Hz)</label>
                  <input
                    type="number"
                    step="0.1"
                    defaultValue={device.sensors.vibration.threshold}
                    onChange={(e) => handleThresholdChange(device.id, "vibration", e.target.value)}
                    className="form-input"
                  />
                </div>
                <div>
                  <button onClick={() => handleSaveThresholds(device.id)} className="btn btn-primary flex items-center">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export Logs */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h3 className="card-title">Export Data</h3>
            <p className="card-subtitle">Download system logs and alert data</p>
          </div>
          <button onClick={onExportLogs} className="btn btn-primary flex items-center">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminPanel
